/** Files ready to POST to Sourcify `/verify` (filename must keep relative path). */
export type VerifyUploadFile = {
  path: string
  blob: Blob
}

export type SolcJsonContractKind = 'contract' | 'interface' | 'library' | 'abstract' | 'unknown'

export type SolcJsonContract = {
  name: string
  path: string
  kind: SolcJsonContractKind
  /** Creation bytecode hex from build-info output when present (may be empty for interfaces). */
  bytecode: string
}

/** Metadata+sources for `/verify`, or raw Standard JSON for `/verify/solc-json`. */
export type PreparedVerification =
  | { kind: 'metadata-files'; files: VerifyUploadFile[] }
  | {
      kind: 'solc-json'
      /** Original Hardhat `input` — do not rewrite outputSelection (bytecode metadata depends on it). */
      input: Record<string, unknown>
      compilerVersion: string
      contracts: SolcJsonContract[]
    }

type HardhatArtifact = {
  metadata?: string
  evm?: { bytecode?: { object?: string } }
}

type HardhatBuildInfo = {
  solcVersion: string
  solcLongVersion?: string
  input: {
    sources?: Record<string, { content?: string }>
    settings?: unknown
    language?: string
  }
  output?: {
    contracts?: Record<string, Record<string, HardhatArtifact>>
  }
}

function uploadPath(file: File): string {
  // Directory picker fills webkitRelativePath as "dir/contracts/Foo.sol".
  // Strip the top-level folder so paths match metadata sources (contracts/..., @openzeppelin/...).
  const relative = file.webkitRelativePath || file.name
  const parts = relative.split('/')
  if (file.webkitRelativePath && parts.length > 1) {
    return parts.slice(1).join('/')
  }
  return relative
}

function isHardhatBuildInfo(value: unknown): value is HardhatBuildInfo {
  if (!value || typeof value !== 'object') return false
  const obj = value as Record<string, unknown>
  const input = obj.input as { sources?: unknown } | undefined
  return (
    typeof obj.solcVersion === 'string' &&
    !!input &&
    typeof input === 'object' &&
    !!input.sources &&
    typeof input.sources === 'object'
  )
}

/** Hardhat 3 npm deps use `npm/@scope/pkg@version/...`; classic uses `@openzeppelin/...` or node_modules. */
export function isDependencySourcePath(sourcePath: string): boolean {
  return (
    sourcePath.startsWith('npm/') ||
    sourcePath.startsWith('@') ||
    sourcePath.includes('/node_modules/') ||
    sourcePath.startsWith('node_modules/') ||
    sourcePath.startsWith('lib/')
  )
}

function bytecodeObject(artifact: HardhatArtifact | undefined): string {
  const raw = artifact?.evm?.bytecode?.object
  return typeof raw === 'string' ? raw.replace(/^0x/i, '') : ''
}

function hasDeployableBytecode(c: SolcJsonContract): boolean {
  return c.bytecode.length > 0
}

function kindFromSource(content: string | undefined, name: string): SolcJsonContractKind {
  if (!content) return 'unknown'
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  if (new RegExp(String.raw`^\s*abstract\s+contract\s+${escaped}\b`, 'm').test(content)) {
    return 'abstract'
  }
  if (new RegExp(String.raw`^\s*interface\s+${escaped}\b`, 'm').test(content)) {
    return 'interface'
  }
  if (new RegExp(String.raw`^\s*library\s+${escaped}\b`, 'm').test(content)) {
    return 'library'
  }
  if (new RegExp(String.raw`^\s*contract\s+${escaped}\b`, 'm').test(content)) {
    return 'contract'
  }
  return 'unknown'
}

function listContractsFromOutput(buildInfo: HardhatBuildInfo): SolcJsonContract[] {
  const out: SolcJsonContract[] = []
  const contracts = buildInfo.output?.contracts ?? {}
  const sources = buildInfo.input.sources ?? {}
  for (const [sourcePath, byName] of Object.entries(contracts)) {
    const content = sources[sourcePath]?.content
    for (const [contractName, artifact] of Object.entries(byName ?? {})) {
      out.push({
        name: contractName,
        path: sourcePath,
        kind: kindFromSource(content, contractName),
        bytecode: bytecodeObject(artifact),
      })
    }
  }
  return out
}

/** Fallback when `output.contracts` is empty: scan Solidity sources for definitions. */
function listContractsFromSources(buildInfo: HardhatBuildInfo): SolcJsonContract[] {
  const out: SolcJsonContract[] = []
  const sources = buildInfo.input.sources ?? {}
  const re =
    /^\s*(abstract\s+contract|contract|library|interface)\s+([A-Za-z_][A-Za-z0-9_]*)/gm
  for (const [sourcePath, entry] of Object.entries(sources)) {
    if (typeof entry?.content !== 'string') continue
    re.lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = re.exec(entry.content)) !== null) {
      const keyword = match[1].replace(/\s+/g, ' ')
      let kind: SolcJsonContractKind = 'unknown'
      if (keyword === 'abstract contract') kind = 'abstract'
      else if (keyword === 'interface') kind = 'interface'
      else if (keyword === 'library') kind = 'library'
      else if (keyword === 'contract') kind = 'contract'
      out.push({ name: match[2], path: sourcePath, kind, bytecode: '' })
    }
  }
  return out
}

/**
 * Contracts shown in the verify picker / used as default.
 * Drops dependency paths (incl. Hardhat 3 `npm/...`), interfaces, and empty-bytecode
 * artifacts when better candidates exist — so IERC20Errors is not selected by mistake.
 */
export function preferProjectContracts(contracts: SolcJsonContract[]): SolcJsonContract[] {
  const notDep = contracts.filter(c => !isDependencySourcePath(c.path))
  const base = notDep.length ? notDep : contracts

  const concrete = base.filter(c => c.kind !== 'interface' && c.kind !== 'abstract')
  const pool = concrete.length ? concrete : base.filter(c => c.kind !== 'interface')
  const finalPool = pool.length ? pool : base

  const withCode = finalPool.filter(hasDeployableBytecode)
  const ranked = withCode.length ? withCode : finalPool

  // Prefer plain `contract` over library when both exist.
  const onlyContracts = ranked.filter(c => c.kind === 'contract' || c.kind === 'unknown')
  return onlyContracts.length ? onlyContracts : ranked
}

function collectMetadataEntries(
  buildInfo: HardhatBuildInfo,
): { name: string; metadata: string }[] {
  const metadataEntries: { name: string; metadata: string }[] = []
  const contracts = buildInfo.output?.contracts ?? {}
  for (const [sourcePath, byName] of Object.entries(contracts)) {
    for (const [contractName, artifact] of Object.entries(byName ?? {})) {
      if (typeof artifact?.metadata !== 'string') continue
      metadataEntries.push({
        name: `${sourcePath.replace(/[\\/]/g, '_')}-${contractName}`,
        metadata: artifact.metadata,
      })
    }
  }
  return metadataEntries
}

function sourcesAsUploadFiles(buildInfo: HardhatBuildInfo): VerifyUploadFile[] {
  const out: VerifyUploadFile[] = []
  const sources = buildInfo.input.sources ?? {}
  for (const [path, entry] of Object.entries(sources)) {
    if (typeof entry?.content !== 'string') continue
    out.push({
      path,
      blob: new Blob([entry.content], { type: 'text/plain' }),
    })
  }
  return out
}

/**
 * Expand a Hardhat `artifacts/build-info/*.json`.
 * - With `output.contracts.*.metadata` → files for `/verify`
 * - Without metadata (Hardhat 3 / trimmed outputSelection) → payload for `/verify/solc-json`
 */
export function prepareFromHardhatBuildInfo(buildInfo: unknown): PreparedVerification {
  if (!isHardhatBuildInfo(buildInfo)) {
    throw new Error('Not a Hardhat build-info JSON (expected solcVersion + input.sources)')
  }

  const compilerVersion = buildInfo.solcLongVersion || buildInfo.solcVersion
  const metadataEntries = collectMetadataEntries(buildInfo)

  if (metadataEntries.length) {
    const files = sourcesAsUploadFiles(buildInfo)
    // Sourcify detects metadata by JSON shape (not filename). Upload all; it matches by bytecode.
    metadataEntries.forEach((entry, index) => {
      files.push({
        path: index === 0 ? 'metadata.json' : `metadata-${entry.name}.json`,
        blob: new Blob([entry.metadata], { type: 'application/json' }),
      })
    })
    return { kind: 'metadata-files', files }
  }

  const contracts = listContractsFromOutput(buildInfo)
  const listed = contracts.length ? contracts : listContractsFromSources(buildInfo)
  if (!listed.length) {
    throw new Error(
      'Hardhat build-info has sources but no contracts to verify (empty output and no contract definitions found)',
    )
  }

  return {
    kind: 'solc-json',
    // Pass input as-is so settings (incl. outputSelection) match the deployed bytecode metadata.
    input: buildInfo.input as Record<string, unknown>,
    compilerVersion,
    contracts: listed,
  }
}

/** @deprecated use prepareFromHardhatBuildInfo — kept for callers expecting files-only expand */
export function expandHardhatBuildInfo(buildInfo: unknown): VerifyUploadFile[] {
  const prepared = prepareFromHardhatBuildInfo(buildInfo)
  if (prepared.kind !== 'metadata-files') {
    throw new Error('Hardhat build-info has sources but no contract metadata in output')
  }
  return prepared.files
}

async function tryPrepareBuildInfoFile(file: File): Promise<PreparedVerification | null> {
  if (!file.name.endsWith('.json')) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(await file.text())
  } catch {
    return null
  }
  if (!isHardhatBuildInfo(parsed)) return null
  return prepareFromHardhatBuildInfo(parsed)
}

/**
 * Turn the user's FileList into a verification payload.
 * - Preserves relative paths (folder upload / webkitRelativePath)
 * - Auto-expands Hardhat build-info JSON (metadata files, or solc-json when metadata is missing)
 */
export async function prepareVerification(fileList: FileList | File[]): Promise<PreparedVerification> {
  const files = Array.from(fileList)
  if (!files.length) {
    return { kind: 'metadata-files', files: [] }
  }

  const passthrough: VerifyUploadFile[] = []
  let solcJson: Extract<PreparedVerification, { kind: 'solc-json' }> | null = null
  const expandedMeta: VerifyUploadFile[] = []

  for (const file of files) {
    const prepared = await tryPrepareBuildInfoFile(file)
    if (prepared?.kind === 'metadata-files') {
      expandedMeta.push(...prepared.files)
      continue
    }
    if (prepared?.kind === 'solc-json') {
      // One build-info drives verification; last wins if several are selected.
      solcJson = prepared
      continue
    }
    passthrough.push({
      path: uploadPath(file),
      blob: file,
    })
  }

  // Metadata expand takes precedence (already has what Sourcify `/verify` needs).
  if (expandedMeta.length) {
    const byPath = new Map<string, VerifyUploadFile>()
    for (const item of [...expandedMeta, ...passthrough]) {
      byPath.set(item.path, item)
    }
    return { kind: 'metadata-files', files: [...byPath.values()] }
  }

  if (solcJson) {
    return solcJson
  }

  return { kind: 'metadata-files', files: passthrough }
}

/** Back-compat: always returns upload files (throws if build-info needs solc-json). */
export async function prepareVerifyUploadFiles(fileList: FileList | File[]): Promise<VerifyUploadFile[]> {
  const prepared = await prepareVerification(fileList)
  if (prepared.kind === 'solc-json') {
    throw new Error('Hardhat build-info has sources but no contract metadata in output')
  }
  return prepared.files
}
